/*
 * Copyright 2026, Airspan
 * All Rights Reserved.
 */

import React, { useCallback, useMemo, useState } from "react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../../components/ui/tooltip";
import CustomDataGrid from "../../components/Shared/Table/CustomDataGrid";
import SearchBox from "../../components/Shared/SearchBox/SearchBox";
import { useAuditDisplay } from "./hooks";
import { goldenConfigurationColumns } from "../../components/Shared/Table/Columns";
import AddAuditDefinitionDialog from "./AddAuditDefinitionDialog";
import EditableTextCell from "../../components/Shared/Table/EditableTextCell";
import { useStoreDispatch } from "../../store/hooks";
import { useSelector } from "react-redux";
import { basicDialogSelector } from "../../store/helpers/helperSelectors";
import { setBasicDialogOpenFor, setOpenToast } from "../../store/helpers/helperReducers";
import { DIALOGOPENFOR } from "../../globals/globals";
import { useUpdateGoldenAuditDescriptionMutation } from "../../store/api/audit/goldenAuditApiSlice";
import { convertSymbols, errorMessage } from "../../utils/helpers";
import { resetKeyword } from "../../store/search/reducer";
import { RadixSelectControl } from "../../components/ui/radix-select";
import { Button } from "../../components/ui/button";
import { FilterX } from "lucide-react";
import { searchKeywordSelector } from "../../store/search/selector";
import ActiveFilterChips from "../../components/Shared/Filter/ActiveFilterChips";
import { Icon } from "../../components/Shared/Icon";
import GoldenConfigurationDetailsDrawer from "../../components/Drawers/GoldenConfigurationDetailsDrawer";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import ProfilePermissions from "../../utils/ProfilePermissions";

const GoldenConfiguration: React.FC = () => {
  const [nodeTypeFilter, setNodeTypeFilter] = useState<string>("All");
  const [technologyFilter, setTechnologyFilter] = useState<string>("All");
  const [lastRunFilter, setLastRunFilter] = useState<string>("All");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const dispatch = useStoreDispatch();
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const [updateGoldenDescription, { isLoading: isUpdating }] = useUpdateGoldenAuditDescriptionMutation();

  const { rows, isLoading } = useAuditDisplay(nodeTypeFilter, technologyFilter, lastRunFilter);

  const searchKeyword = useSelector(searchKeywordSelector);

  const showReset = nodeTypeFilter !== "All" || technologyFilter !== "All" || lastRunFilter !== "All" || (!!searchKeyword && searchKeyword.trim().length > 0);

  const handleClearFilters = useCallback(() => {
    setNodeTypeFilter("All");
    setTechnologyFilter("All");
    setLastRunFilter("All");
    dispatch(resetKeyword());
  }, [dispatch]);

  const handleClearSearch = useCallback(() => {
    dispatch(resetKeyword());
  }, [dispatch]);

  const goldenConfigFilters = useMemo(() => [
    {
      id: "nodeType",
      label: "Node Type",
      value: nodeTypeFilter !== "All" ? nodeTypeFilter : "",
      setValue: ((_: any) => { setNodeTypeFilter("All"); return { type: "NOOP" }; }) as any,
      values: [
        { value: "SN", text: "SN" },
        { value: "CU", text: "CU" },
        { value: "RCP", text: "RCP" },
        { value: "ME1200", text: "ME1200" },
      ],
    },
    {
      id: "technology",
      label: "Technology",
      value: technologyFilter !== "All" ? technologyFilter : "",
      setValue: ((_: any) => { setTechnologyFilter("All"); return { type: "NOOP" }; }) as any,
      values: [
        { value: "LTEONLY", text: "LTE" },
        { value: "NR", text: "NR" },
      ],
    },
    {
      id: "lastRun",
      label: "Last Run",
      value: lastRunFilter !== "All" ? lastRunFilter : "",
      setValue: ((_: any) => { setLastRunFilter("All"); return { type: "NOOP" }; }) as any,
      values: [
        { value: "Pass", text: "Pass" },
        { value: "Fail", text: "Fail" },
        { value: "Skipped", text: "Skipped" },
      ],
    },
  ], [nodeTypeFilter, technologyFilter, lastRunFilter]);

  const handleEditStart = useCallback((row: any) => {
    setEditingId(row.id);
    setEditingValue(row.description ?? "");
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingId(null);
    setEditingValue("");
  }, []);

  const handleEditSave = useCallback(
    async (row: any) => {
      const newDescription = editingValue ?? "";
      const currentDescription = row.description ?? "";
      if (newDescription === currentDescription) {
        handleEditCancel();
        return;
      }

      try {
        await updateGoldenDescription({ id: row.id, description: newDescription }).unwrap();
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: "Description updated.",
          })
        );
        handleEditCancel();
      } catch (error: any) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    },
    [dispatch, editingValue, handleEditCancel, updateGoldenDescription]
  );

  const descriptionColumns = useMemo(() => {
    return goldenConfigurationColumns.map((column) => {
      if (column.field !== "description") {
        return column;
      }

      return {
        ...column,
        editable: false,
        renderCell: (cellValues: any) => {
          const row = cellValues.row;
          const isEditing = editingId === row.id;
          const displayValue = convertSymbols(row.description ?? "");

          return (
            <EditableTextCell
              value={row.description ?? ""}
              displayValue={displayValue}
              isEditing={isEditing}
              editValue={editingValue}
              onEditValueChange={setEditingValue}
              onEditStart={() => handleEditStart(row)}
              onCancel={handleEditCancel}
              onSave={() => handleEditSave(row)}
              isSaving={isUpdating}
            />
          );
        },
      };
    });
  }, [editingId, editingValue, handleEditCancel, handleEditSave, handleEditStart, isUpdating]);

  const getRowClassName = useCallback((params: any) => {
    return params?.row?.scheduleEnabled === false ? "gold-config-disabled" : "";
  }, []);

  const handleRowClick = useCallback((params: any) => {
    setSelectedRow(params.row);
    setDetailsOpen(true);
  }, []);

  return (
    <div className="flex flex-col w-full max-w-full overflow-x-hidden">
      <div className="mb-2">
        <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-2 w-full max-w-full overflow-x-hidden">
          <div className="flex items-center flex-wrap gap-2 min-w-0 max-w-full flex-1">
            <div style={{ marginRight: 1 }} className="shrink-0">
              <SearchBox tooltip="Search configurations..." placeholder="Search configurations..." />
            </div>
            <div style={{ width: "140px" }}>
              <RadixSelectControl
                value={nodeTypeFilter === "All" ? "__placeholder__" : nodeTypeFilter}
                onValueChange={(val) => setNodeTypeFilter(val === "__placeholder__" ? "All" : val)}
                options={[
                  { value: "__placeholder__", label: "Node Type" },
                  { value: "SN", label: "SN" },
                  { value: "CU", label: "CU" },
                  { value: "RCP", label: "RCP" },
                  { value: "ME1200", label: "ME1200" },
                ]}
                placeholder="Node Type"
              />
            </div>
            <div style={{ width: "140px" }}>
              <RadixSelectControl
                value={technologyFilter === "All" ? "__placeholder__" : technologyFilter}
                onValueChange={(val) => setTechnologyFilter(val === "__placeholder__" ? "All" : val)}
                options={[
                  { value: "__placeholder__", label: "Technology" },
                  { value: "LTEONLY", label: "LTE" },
                  { value: "NR", label: "NR" },
                ]}
                placeholder="Technology"
              />
            </div>
            <div style={{ width: "160px" }}>
              <RadixSelectControl
                value={lastRunFilter === "All" ? "__placeholder__" : lastRunFilter}
                onValueChange={(val) => setLastRunFilter(val === "__placeholder__" ? "All" : val)}
                options={[
                  { value: "__placeholder__", label: "Last Run" },
                  { value: "Pass", label: "Pass" },
                  { value: "Fail", label: "Fail" },
                  { value: "Skipped", label: "Skipped" },
                ]}
                placeholder="Last Run"
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="inline-flex items-center justify-center rounded-md text-sm h-8 w-8 bg-muted transition-colors duration-150 hover:bg-muted"
                  onClick={handleClearFilters}
                  aria-label="Reset All"
                  style={{ visibility: showReset ? "visible" : "hidden", marginLeft: "7px" }}
                >
                  <FilterX className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset All</TooltipContent>
            </Tooltip>
          </div>
          {permissions?.hasOpCodeGroup("task_management") && (
            <div className="flex items-center shrink-0 ml-auto">
              <Button
                variant="outline"
                className="h-10 px-4 text-sm font-bold border border-input bg-background shadow-sm hover:bg-muted hover:text-accent-foreground transition-colors"
                style={{ minWidth: "180px" }}
                onClick={() => dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.AUDIT))}
              >
                <Icon name="add" size={18} />
                Add Configuration
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2 mt-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mb-1">{`Showing ${rows?.length || 0} results`}</span>
          <ActiveFilterChips
            filters={goldenConfigFilters}
            searchKeyword={searchKeyword}
            onClearSearch={handleClearSearch}
            onClearAll={handleClearFilters}
            dispatch={dispatch}
          />
        </div>

      </div>
      <div className="w-full max-w-full">
        <div className="w-full max-w-full overflow-x-auto">
          <CustomDataGrid
            tableId="goldenConfiguration"
            sx={{
              minHeight: "440px",
              "& .gold-config-disabled": {
                opacity: 0.55,
              },
            }}
            rows={rows}
            columns={descriptionColumns}
            rowCount={rows.length}
            loading={isLoading}
            paginationMode="client"
            sortingMode="client"
            initialSortModel={[{ field: "lastRun", sort: "desc" }]}
            disableColumnMenu={true}
            hideFooter={true}
            getRowClassName={getRowClassName}
            disableRowSelectionOnClick={true}
            onRowClick={handleRowClick}
            fillContainer
          />
        </div>
      </div>
      {basicDialogOpenFor === DIALOGOPENFOR.AUDIT && <AddAuditDefinitionDialog />}
      <GoldenConfigurationDetailsDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        row={selectedRow}
      />
    </div>
  );
};

export default GoldenConfiguration;
